import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import AuthLayout from '../components/AuthLayout';
import { Input } from '../components/forms';
import { authService } from '../services/authService';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.forgotPassword({ email: data.email });
      if (response.success) {
        // Redirect to OTP verification page with email
        navigate('/reset-password-otp', { state: { email: data.email } });
      } else {
        setError(response.message || t('auth.failedToSendResetCode'));
      }
    } catch {
      setError(t('auth.anErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('auth.forgotPassword')}
      subtitle="Enter your registered email and we'll send you a verification code to reset your password"
      backTo="/login"
    >
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email field */}
        <div className="space-y-1">
          <label className="text-xs font-normal text-[#1F1F1F] leading-[1.6666666666666667em]">
            {t('auth.email')}
          </label>
          <Input
            {...register('email', {
              required: t('auth.errors.emailRequired'),
              pattern: {
                value: /^\S+@\S+$/i,
                message: t('auth.errors.validEmailRequired'),
              },
            })}
            type="email"
            placeholder={t('auth.enterYourEmail')}
            error={errors.email?.message}
            className="w-full border border-[#DEDEE4] rounded-md px-4 py-[14px] text-base font-normal text-[#6E7077] leading-[1.75em] focus:border-brand-primary focus:ring-brand-primary"
          />
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white font-bold py-[14px] px-[30px] rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm leading-[1.2102272851126534em] uppercase tracking-wide"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              <span>{t('auth.sending')}</span>
            </div>
          ) : (
            'SEND RESET CODE'
          )}
        </button>

        {/* Back to login link */}
        <div className="text-right pt-2">
          <p className="text-base font-normal text-[#1F1F1F] leading-[1.75em]">
            Remember your password?{' '}
            <Link
              to="/login"
              className="text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
            >
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;
